// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ChevronDown, Clock, Package, Pencil, ThumbsDown, ThumbsUp, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  fieldAppliesToChain,
  interpolateTemplate,
  referencedChains,
  resolveFieldPath,
  SAMPLE_TASK,
} from "@/features/tasks/sample-task";
import { TaskCardChip } from "@/features/tasks/task-card-preview/task-card-chip";
import { useRobotoFont } from "@/features/tasks/task-card-preview/use-roboto-font";
import { TaskIcon } from "@/features/tasks/task-icon";
import { type TaskCardSettings, type TaskCardTab, tabContentType, tabKey } from "@/features/tasks/types";
import { cn } from "@/lib/utils";

/**
 * A still of the Action Tool's task card, driven entirely by a `TaskCardSettings`.
 *
 * Measurements are lifted from the real components rather than approximated — `TaskCardHeader`,
 * `TaskCardLocation`, `TaskCardDetailsButtons`, `TaskCardContent` and `TaskActionButton` in
 * `web-focal-os/libs/tasks`. The palette is raw hex on purpose: this has to look like the
 * handheld in both back-office themes, so it must not follow the theme tokens.
 */

/** Colours the Action Tool hard-codes; see TaskCardDetailsButtons / TaskActionButton. */
const TAB_INACTIVE = "#666666";
const TAB_ACTIVE = "#419488";
const PANEL = "#EDEDED";
const ACTION_PRIMARY = "#04877C";
const ACTION_WARNING = "#FDB838";

/** Default tab labels, from `buttons.*.default` in the Action Tool's `tasks` namespace. */
const TAB_LABELS: Record<string, string> = {
  image: "Image",
  info: "Info",
  issues: "Issues",
  location: "Map",
  planogram: "POG",
  raw_planogram: "POG",
  pog_new: "New Mod",
  pog_old: "Old Mod",
  product: "Product",
  bay_image: "Bay Image",
  discrepancy_image: "Image",
  imageSlider: "Images",
  interactiveTemplate: "Image",
  interactivePog: "POG",
};

/** Editable regions of the card, in the order they appear top to bottom. */
export type TaskCardSection =
  | "identity"
  | "header"
  | "subHeader"
  | "topContent"
  | "tabs"
  | "infoFields"
  | "actions"
  | "snooze"
  | "toggles";

/** Blend a hex colour towards white, the way MUI's `lighten` does. */
function lighten(hex: string, amount: number): string {
  const parsed = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!parsed?.[1]) return hex;
  const value = Number.parseInt(parsed[1], 16);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  const r = mix((value >> 16) & 0xff);
  const g = mix((value >> 8) & 0xff);
  const b = mix(value & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/** The label the Action Tool prints on a tab. */
export function resolveTabLabel(tab: TaskCardTab): string {
  if (typeof tab !== "string") return tab.tabLabels?.default ?? tab.name;
  return TAB_LABELS[tab] ?? tab;
}

/**
 * The header title the Action Tool would show for this header mode.
 *
 * Mirrors `TaskCardHeader`'s `taskCardHeaderLabel`, including its preference for the item's shelf
 * tag name over the aisle/side/mod triple in `location` mode.
 */
export function resolveHeaderLabel(settings: TaskCardSettings, fallback: string): string {
  const item = (SAMPLE_TASK.item ?? {}) as Record<string, unknown>;
  const taskTypePart = settings.shortName || settings.fullName;
  switch (settings.headerMode) {
    case "text":
      return settings.headerText || fallback;
    case "template":
      return settings.headerText ? interpolateTemplate(settings.headerText) : fallback;
    case "fullTaskType":
      return settings.fullName;
    case "shortTaskType":
      return taskTypePart;
    case "productName":
      return [taskTypePart, item.name].filter(Boolean).join(" - ");
    case "location": {
      // A shelf tag name wins outright; only without one does it fall back to aisle/side/mod
      if (item.shelfTagName) return [taskTypePart, item.shelfTagName].filter(Boolean).join(" - ");
      const parts = [
        taskTypePart,
        item.aisle ? `Aisle ${item.aisle}` : null,
        item.aisleSide ? `Side ${item.aisleSide}` : null,
        item.mod ? `Mod ${item.mod}` : null,
      ].filter(Boolean);
      return parts.join(" - ");
    }
    default:
      return fallback;
  }
}

interface EditableProps {
  section: TaskCardSection;
  activeSection?: TaskCardSection | null;
  onSelect?: (section: TaskCardSection) => void;
  label: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Marks a card region as editable.
 *
 * The region stays a plain container — the click target is a pencil in its corner. Wrapping the
 * region in a button would nest the tab strip's own buttons inside it.
 */
function Region({ section, activeSection, onSelect, label, className, children }: EditableProps) {
  if (!onSelect) return <div className={className}>{children}</div>;
  return (
    <div
      className={cn(
        "group/region relative transition-shadow",
        "hover:ring-2 hover:ring-inset hover:ring-primary/60",
        activeSection === section && "ring-2 ring-inset ring-primary",
        className,
      )}
    >
      {children}
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => onSelect(section)}
        className={cn(
          "absolute right-1 top-1 z-20 inline-flex h-5 w-5 items-center justify-center rounded",
          "bg-primary text-primary-foreground opacity-0 shadow transition-opacity",
          "group-hover/region:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          activeSection === section && "opacity-100",
        )}
      >
        <Pencil className="h-3 w-3" />
      </button>
    </div>
  );
}

interface TaskCardPreviewProps {
  settings: TaskCardSettings;
  /** Chain the preview renders for — drives the `chains` filter on field lists. */
  chainName?: string;
  /** Supplying this turns the still into the editor's canvas. */
  onSelectSection?: (section: TaskCardSection) => void;
  activeSection?: TaskCardSection | null;
  className?: string;
}

export function TaskCardPreview({
  settings,
  chainName,
  onSelectSection,
  activeSection,
  className,
}: TaskCardPreviewProps) {
  const { t } = useTranslation("back-office");
  useRobotoFont();
  // Which tab is open is the preview's own view state, not an edit — it stays switchable whether
  // or not the card is being edited.
  const [selectedTab, setSelectedTab] = useState<string | undefined>(undefined);

  // Fields can be guarded to a chain, so the card genuinely differs per chain. Offer exactly the
  // chains this card names, defaulting to the scope the editor is on, else the first one — with no
  // chain at all every guarded field would be hidden, which reads as a broken card.
  const chains = useMemo(
    () => referencedChains([settings.topContentFields, settings.infoTabFields]),
    [settings.topContentFields, settings.infoTabFields],
  );
  const [pickedChain, setPickedChain] = useState<string | undefined>(undefined);
  const previewChain =
    pickedChain ?? (chainName && chains.includes(chainName) ? chainName : chains[0]) ?? chainName ?? undefined;

  const headerFontColor = settings.headerFontColor || "#FFFFFF";
  const subHeaderColor = settings.subHeaderColor || lighten(settings.color, 0.5);
  // TaskCardTitleValue: the value takes the configured colour, the label a lightened one
  const subHeaderValueColor = settings.subHeaderFontColor || "#000000";
  const subHeaderLabelColor = settings.subHeaderFontColor || lighten("#000000", 0.33);
  const headerLabel = resolveHeaderLabel(settings, t("tasks.preview.untitledHeader"));

  // With no tabs configured the Action Tool falls back to its own defaults
  const tabs: TaskCardTab[] = settings.tabs.length ? settings.tabs : ["image", "info", "location"];
  const firstTab = tabs[0] ?? "info";
  const activeTab = selectedTab && tabs.some((tab) => tabKey(tab) === selectedTab) ? selectedTab : tabKey(firstTab);
  const activeContent = tabs.find((tab) => tabKey(tab) === activeTab) ?? firstTab;
  const activeTabIndex = tabs.findIndex((tab) => tabKey(tab) === activeTab);
  // A single tab is not worth a tab bar, so the panel keeps all four corners rounded
  const hasTabBar = tabs.length > 1;

  const topFields = settings.topContentFields.filter(
    (field) => field.scope !== "reverseScanDrawer" && fieldAppliesToChain(field.chains, previewChain),
  );
  const infoFields = settings.infoTabFields.filter((field) => fieldAppliesToChain(field.chains, previewChain));

  // A configured start action replaces the worked / not-worked block outright — see TaskCardActions
  const hasStartActions = settings.startActions.length > 0;
  const hasSecondaryButtons =
    settings.enableSnoozeButton ||
    settings.enableInventoryButton ||
    settings.enableDeleteTaskButton ||
    settings.isPickable;

  return (
    <div className={cn("w-[450px] max-w-full shrink-0", className)}>
      <div
        className={
          // 450px is the Action Tool's default card width — a fixed width, not a ceiling.
          "task-card-preview overflow-hidden rounded-lg bg-white text-neutral-900 shadow-lg ring-1 ring-black/10"
        }
      >
        {/* Header — colour, icon and title */}
        <Region
          section="header"
          activeSection={activeSection}
          onSelect={onSelectSection}
          label={t("tasks.preview.regions.header")}
          className="relative z-10"
        >
          <div
            className="flex min-h-8 items-center gap-1.5 rounded-b-lg px-2"
            style={{ backgroundColor: settings.color, color: headerFontColor }}
          >
            <TaskIcon icon={settings.icon} color={headerFontColor} />
            <span className="line-clamp-2 flex-1 py-1 text-[16px] font-semibold uppercase leading-[18px]">
              {headerLabel}
            </span>
            {settings.enableCompress && <ChevronDown className="h-5 w-5 shrink-0" style={{ color: headerFontColor }} />}
          </div>
        </Region>

        {/* Sub-header — tucks under the header's rounded bottom, as TaskCardLocation does */}
        {settings.subHeaderMode !== "hidden" && (
          <Region
            section="subHeader"
            activeSection={activeSection}
            onSelect={onSelectSection}
            label={t("tasks.preview.regions.subHeader")}
          >
            <div
              className="-mt-3 flex flex-wrap items-center gap-x-2 rounded-b-lg px-2 pb-1 pt-4 text-[12px] leading-5"
              style={{ backgroundColor: subHeaderColor }}
            >
              {settings.subHeaderMode === "text" && (
                <span className="px-2" style={{ color: subHeaderValueColor }}>
                  {settings.subHeaderText || t("tasks.preview.noSubHeaderText")}
                </span>
              )}
              {settings.subHeaderMode === "template" && (
                <span className="px-2" style={{ color: subHeaderValueColor }}>
                  {settings.subHeaderText
                    ? interpolateTemplate(settings.subHeaderText)
                    : t("tasks.preview.noSubHeaderText")}
                </span>
              )}
              {(!settings.subHeaderMode || settings.subHeaderMode === "location") && (
                <>
                  <SubHeaderPair
                    label={t("tasks.preview.location.department")}
                    value="DSD DAIRY (42)"
                    labelColor={subHeaderLabelColor}
                    valueColor={subHeaderValueColor}
                  />
                  <SubHeaderPair
                    label={t("tasks.preview.location.aisle")}
                    value="GR01"
                    labelColor={subHeaderLabelColor}
                    valueColor={subHeaderValueColor}
                  />
                  <SubHeaderPair
                    label={t("tasks.preview.location.side")}
                    value="L"
                    labelColor={subHeaderLabelColor}
                    valueColor={subHeaderValueColor}
                  />
                  <SubHeaderPair
                    label={t("tasks.preview.location.mod")}
                    value="4"
                    labelColor={subHeaderLabelColor}
                    valueColor={subHeaderValueColor}
                  />
                </>
              )}
            </div>
          </Region>
        )}

        {/* Top content — the chip block under the sub-header. Absent when nothing is configured, the
          way the card renders it; the Edit tab still reaches it through its section rail. */}
        {topFields.length > 0 && (
          <Region
            section="topContent"
            activeSection={activeSection}
            onSelect={onSelectSection}
            label={t("tasks.preview.regions.topContent")}
            className="px-3 pt-2"
          >
            <div className="grid grid-cols-12 gap-x-2 gap-y-1">
              {topFields.map((field, index) => (
                <div
                  key={`${field.field}-${field.label}-${index}`}
                  style={{ gridColumn: `span ${Math.min(12, Math.max(1, field.gridSize ?? 12))}` }}
                  className="min-w-0"
                >
                  {field.type !== "space" && (
                    <TaskCardChip
                      label={field.label}
                      value={resolveFieldPath(SAMPLE_TASK, field.field)}
                      type={field.type}
                      color={field.color}
                      variant={field.variant}
                      chainName={previewChain}
                    />
                  )}
                </div>
              ))}
            </div>
          </Region>
        )}

        {/* Tab bar — full width, the active tab sits on the content panel's grey */}
        {hasTabBar && (
          <Region
            section="tabs"
            activeSection={activeSection}
            onSelect={onSelectSection}
            label={t("tasks.preview.regions.tabs")}
            className="mt-2 px-2"
          >
            <div className="flex">
              {tabs.map((tab) => {
                const key = tabKey(tab);
                const isActive = key === activeTab;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedTab(key)}
                    style={{
                      color: isActive ? TAB_ACTIVE : TAB_INACTIVE,
                      backgroundColor: isActive ? PANEL : "transparent",
                    }}
                    className="min-w-0 flex-1 cursor-pointer truncate rounded-t-lg px-1 py-2 text-[14px] font-bold uppercase leading-[1.75] tracking-[0.02857em]"
                  >
                    {resolveTabLabel(tab)}
                  </button>
                );
              })}
            </div>
          </Region>
        )}

        {/* Tab body — the grey content panel. The corner under the selected tab stays square so the
          tab reads as continuous with the panel; every other corner is rounded. */}
        <div className="px-2">
          <div
            className={cn(
              // 250px is the Action Tool's default tab content height
              "min-h-[250px] rounded-b-lg p-2",
              !(hasTabBar && activeTabIndex === 0) && "rounded-tl-lg",
              !(hasTabBar && activeTabIndex === tabs.length - 1) && "rounded-tr-lg",
            )}
            style={{ backgroundColor: PANEL }}
          >
            {tabContentType(activeContent) === "info" ? (
              <Region
                section="infoFields"
                activeSection={activeSection}
                onSelect={onSelectSection}
                label={t("tasks.preview.regions.infoFields")}
                className="rounded bg-white p-2"
              >
                {infoFields.length ? (
                  <div className="space-y-1">
                    {infoFields.map((field, index) => (
                      <TaskCardChip
                        key={`${field.field}-${field.label}-${index}`}
                        label={field.label}
                        value={resolveFieldPath(SAMPLE_TASK, field.field)}
                        type={field.type}
                        color={field.color}
                        variant={field.variant}
                        chainName={previewChain}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyHint text={t("tasks.preview.noInfoFields")} />
                )}
              </Region>
            ) : (
              // The real tabs render shelf photos, planograms and store maps, none of which the back
              // office has for a sample task. The panel names the selected tab and the content type
              // behind it instead, so switching tabs still tells the author what they configured.
              // Fills the 250px panel less its 8px padding, so it sits centred in it.
              <div className="flex min-h-[234px] flex-col items-center justify-center gap-1 text-neutral-400">
                <span className="text-[13px] font-medium uppercase tracking-wide">
                  {resolveTabLabel(activeContent)}
                </span>
                <span className="font-mono text-[11px]">{tabContentType(activeContent)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <Region
          section="actions"
          activeSection={activeSection}
          onSelect={onSelectSection}
          label={t("tasks.preview.regions.actions")}
          className="p-2"
        >
          {hasStartActions ? (
            <div className="flex gap-2">
              {settings.startActions.map((action, index) => (
                <ActionButton
                  key={`${action.displayName}-${index}`}
                  label={action.displayName}
                  icon={action.icon ?? "PlayCircleOutline"}
                  color={action.color}
                  variant={action.variant}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-1.5">
              <ActionRow
                icon={<ThumbsUp className="h-4 w-4" />}
                tone="worked"
                actions={settings.workedActions.map((action) => action.displayName)}
                emptyText={t("tasks.preview.noWorkedActions")}
              />
              <ActionRow
                icon={<ThumbsDown className="h-4 w-4" />}
                tone="notWorked"
                actions={settings.notWorkedActions.map((action) => action.displayName)}
                emptyText={t("tasks.preview.noNotWorkedActions")}
              />
            </div>
          )}
        </Region>

        {/* Secondary buttons driven by the toggles — the strip is absent when none are on, the way
          the card renders it. The Edit tab still reaches these through its section rail. */}
        {hasSecondaryButtons && (
          <Region
            section="toggles"
            activeSection={activeSection}
            onSelect={onSelectSection}
            label={t("tasks.preview.regions.toggles")}
            className="flex flex-wrap items-center gap-2 border-t border-neutral-200 px-3 py-2"
          >
            {settings.enableSnoozeButton && (
              <SecondaryButton icon={<Clock className="h-3.5 w-3.5" />} label={t("tasks.preview.buttons.snooze")} />
            )}
            {settings.enableInventoryButton && (
              <SecondaryButton
                icon={<Package className="h-3.5 w-3.5" />}
                label={t("tasks.preview.buttons.inventory")}
              />
            )}
            {settings.enableDeleteTaskButton && (
              <SecondaryButton icon={<Trash2 className="h-3.5 w-3.5" />} label={t("tasks.preview.buttons.delete")} />
            )}
            {settings.isPickable && (
              <SecondaryButton icon={<Package className="h-3.5 w-3.5" />} label={t("tasks.preview.buttons.pick")} />
            )}
          </Region>
        )}
      </div>
      {/* Chain switcher — only for cards that actually differ per chain */}
      {chains.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">{t("tasks.preview.previewAs")}</span>
          {chains.map((chain) => (
            <Button
              key={chain}
              type="button"
              size="sm"
              variant={chain === previewChain ? "default" : "outline"}
              onClick={() => setPickedChain(chain)}
              className="h-6 px-2 font-mono text-[11px]"
            >
              {chain}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function SubHeaderPair({
  label,
  value,
  labelColor,
  valueColor,
}: {
  label: string;
  value: string;
  labelColor: string;
  valueColor: string;
}) {
  return (
    <span className="whitespace-nowrap px-2">
      <span style={{ color: labelColor }}>{label}</span> <span style={{ color: valueColor }}>{value}</span>
    </span>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <span className="text-[11px] italic text-neutral-400">{text}</span>;
}

/** A contained action button, styled like the Action Tool's `TaskActionButton`. */
function ActionButton({
  label,
  icon,
  color,
  variant,
}: {
  label: string;
  icon?: string;
  color?: string;
  variant?: string;
}) {
  const contained = (variant ?? "contained") === "contained";
  const background = color === "warning" ? ACTION_WARNING : ACTION_PRIMARY;
  return (
    <span
      className={cn(
        "flex flex-1 items-center justify-center gap-2 rounded px-4 py-2 text-[14px] font-medium leading-5 tracking-[0.1px]",
        !contained && "border border-neutral-300 text-neutral-700",
      )}
      style={contained ? { backgroundColor: background, color: "#FFFFFF" } : undefined}
    >
      {icon && <TaskIcon icon={icon} className="h-4 w-4" color={contained ? "#FFFFFF" : undefined} />}
      {label}
    </span>
  );
}

function ActionRow({
  icon,
  tone,
  actions,
  emptyText,
}: {
  icon: React.ReactNode;
  tone: "worked" | "notWorked";
  actions: string[];
  emptyText: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <span
        className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-white"
        style={{ backgroundColor: tone === "worked" ? "#1E9E6A" : "#D93A4B" }}
      >
        {icon}
      </span>
      {actions.length ? (
        <div className="flex flex-wrap gap-1.5">
          {actions.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className="rounded border border-neutral-300 bg-white px-3 py-1.5 text-[13px] font-medium leading-5 text-neutral-800"
            >
              {name}
            </span>
          ))}
        </div>
      ) : (
        <EmptyHint text={emptyText} />
      )}
    </div>
  );
}

function SecondaryButton({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-neutral-300 bg-neutral-50 px-1.5 py-0.5 text-[11px] text-neutral-700">
      {icon}
      {label}
    </span>
  );
}
